import DeleteIcon from '@mui/icons-material/Delete';
import { Button, Drawer, ToggleButton, ToggleButtonGroup } from '@mui/material';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import CustomTableGrid from 'components/uiComponents/CustomTableGrid';
import FormikControl from 'components/wrapperComponents/FormikControl';
import { msgTypes } from 'constants/msgTypes';
import { Form, Formik } from 'formik';
import { InvoiceManagementModel } from 'model/screensModels/InvoiceManagementModel';
import { PaginatorModel } from 'model/utilModels/PaginatorModel';
import { ResponseModel } from 'model/utilModels/ResponseModel';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { LocalStorageService } from 'services/auth/LocalStorageService';
import { InvoiceManagementService } from 'services/menu/invoiceManagementService/InvoiceManagementService';
import { conformationAlert } from 'services/shared/alert';
import DisplayParticularInvoiceAsDrawer from './DisplayParticularInvoiceAsDrawer';
interface MyInvoiceProps {
  setFlag: (flag: boolean) => void;
  updateRecord: (record: InvoiceManagementModel) => void;
}

const OwedInvoice: React.FC<MyInvoiceProps> = ({ setFlag, updateRecord }) => {
  const location = useLocation();
  const [owedInvoiceList, setOwedInvoiceList] =  useState<ResponseModel | null>(null); 
  const [open, setOpen] = React.useState(false);
  const [selectedInvoice, setSelectedInvoice] = React.useState(new InvoiceManagementModel());
  const [exportAsExcel, setExportAsExcel] = useState({})
  const [pagination, setPagination] = useState({
    pageSize: new PaginatorModel().pageSize,
    pageNumber: new PaginatorModel().pageNumber,
    filter: "",
    orderBy: "",
    desc: "",
  });
  const [activeTab, setActiveTab] = useState(0);
  const [status, setStatus] = React.useState(msgTypes.INVOICE_STATUS.PAID);
  const [messageRead, setMessageRead] = useState(false)

  const columns = useMemo(() =>
    [
      {
        header: "View",
        Cell: ({ renderedCellValue, row }) => (
          <Box className="abcccc" sx={{ display: "flex", alignItems: "center" }}>
            <Button onClick={toggleDrawer(true, row.original)} >{row.original.toReadStatus ? <span className="mt-2 badge badge-sm bg-gradient-warning">View</span> : <span className="mt-2 badge badge-sm bg-gradient-success">view</span>} </Button>
          </Box>
        ),
        size: 50,
      },
      {
        accessorKey: "invoiceId",
        header: "Invoice Number",
        size: 100
      },
      {
        accessorKey: "fromCompanyName",
        header: "From Company Name",
        size: 150
      },
 
      {
        accessorKey: "amount",
        header: "Amount",
        size: 80,
        Cell: ({ row }) => {
          const formattedAmount = new Intl.NumberFormat('en-IN', {
            maximumFractionDigits: 0
          }).format(row.original.amount);
          return <span>{formattedAmount}</span>;
        }
      },
      {
        accessorKey: "currency",
        header: "Currency",
        size: 80
      },
      {
        accessorKey: "subject",
        header: "Subject",
        size: 150
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 120,
        Cell: ({ row }) => {
          const status = row.original.status;
          let statusStyle = {};
          let statusText = '';

          switch (status) {
            case 'Paid':
              statusStyle = { color: '#4cae4c', fontWeight: 'bold' };
              statusText = '✔ Paid';
              break;
            case 'Overdue':
              statusStyle = { color: '#d43f3a', fontWeight: 'bold' };
              statusText = '⚠ Overdue';
              break;
            case 'Outstanding':
              statusStyle = { color: '#ec971f', fontWeight: 'bold' };
              statusText = '⏳ Outstanding';
              break;
            default:
              statusStyle = { color: 'grey' }; // Default status style
              statusText = status;
          }

          return (
            <span style={statusStyle}>
              {statusText}
            </span>
          );
        }
      },
      {
        accessorKey: "uploadingDate",
        header: "Invoice Uploading Date"
      },

      {
        header: 'Action',
        Cell: ({ renderedCellValue, row }) => (
          <div className='d-flex'>
            <Tooltip title="Delete">
              <DeleteIcon fontSize="small" onClick={() => { deleteRecord(row.original) }} />
            </Tooltip>
          </div>
        ),
        size: 10,
        grow: false,
      },



    ], []);


  useEffect(() => {
    getInvoiceList();
  }, [activeTab, pagination.pageNumber, pagination.pageSize, pagination.filter, pagination.orderBy, pagination.desc])

  useEffect(() => {
    if (open === false) {
      getInvoiceList();
    }
  }, [open])

  const prepareRequest = async () => {
    const requestBody = new InvoiceManagementModel();
    requestBody.pageNumber = pagination.pageNumber;
    requestBody.pageSize = pagination.pageSize;
    requestBody.filter = pagination.filter;
    requestBody.orderBy = pagination.orderBy;
    requestBody.desc = pagination.desc;

    if (activeTab === 0) {
      requestBody.status = msgTypes.INVOICE_STATUS.PAID
    } else if (activeTab === 1) {
      requestBody.status = msgTypes.INVOICE_STATUS.OUTSTANDING
    } else {
      requestBody.status = msgTypes.INVOICE_STATUS.OVERDUE
    }

    const userDetails = JSON.parse(await LocalStorageService.getUserDetails());
    requestBody.toCompanyId = userDetails.companyId;

    return requestBody;
  };


  const getInvoiceList = async () => {
    const requestBody = await prepareRequest();
    try {
      var res = new ResponseModel();
      if (location.state?.message === msgTypes.UNREAD_INVOICE) {
        res = await InvoiceManagementService.unreadInvoiceList(requestBody);
      } else {
        res = await InvoiceManagementService.getInvoiceList(requestBody);
      }
      if (res.statusMsg === msgTypes.SUCCESS.msg) {
        let filterData = undefined;
        if (res.data !== '') {
          setOwedInvoiceList(res);
          filterData = res.data.tbody.map((data: InvoiceManagementModel) => {
            return {
              "Invoice Id": data.invoiceId,
              "To Company Name": data.toCompanyName,
              "Amount": data.amount,
              "Currency": data.currency,
              "Status": data.status,
              "Subject": data.subject,
              "Uploading Date": data.uploadingDate
            }
          })
          setExportAsExcel({ fileName: "InwardInvoices.xlsx", data: filterData })
        } else {
          setOwedInvoiceList(new ResponseModel());
          setExportAsExcel({ fileName: "InwardInvoices.xlsx", data: [] })
        }
      }
    } catch (error) {
      console.error('Failed to fetch invoice list:', error);
    }
  };

  const handleTabChange = (index: number) => {
    setActiveTab(index);
  };

  const editRecord = (record) => {
    updateRecord(record);
    setFlag(true)
  }

  const deleteRecord = async (record: InvoiceManagementModel) => {
    const requestData: InvoiceManagementModel = new InvoiceManagementModel();
    requestData.invoiceId = record.invoiceId;

    const deleteFlag = await conformationAlert(msgTypes.CONFORMATION_MESSAGE.DELETE_RECORD, msgTypes.CONFORMATION_ICON.ERROR, "delete");
    if (deleteFlag) {
      const res = await InvoiceManagementService.deleteInvoice(requestData);
      if (res.statusMsg === msgTypes.SUCCESS.msg) {
        getInvoiceList();
      }
    }
  };

  const initialValues = {};
  const onSubmit = () => {
    getInvoiceList();
  };



  const toogleHandleChange = (
    event: React.MouseEvent<HTMLElement>,
    newStatus: string,
  ) => {
    setStatus(newStatus);
  };

  const toggleDrawer = (newOpen: boolean, invoice: InvoiceManagementModel) => () => {
    setSelectedInvoice(invoice)
    setOpen(newOpen);
  };

  return (
    <>
      <ToggleButtonGroup
        color="primary"
        value={status}
        exclusive
        onChange={toogleHandleChange}
        aria-label="Platform"
        className='but---'
      >
        <ToggleButton className='invoice-button button-success' value={msgTypes.INVOICE_STATUS.PAID} onClick={() => handleTabChange(0)}>{msgTypes.INVOICE_STATUS.PAID}</ToggleButton>
        <ToggleButton className='invoice-button button-warning' value={msgTypes.INVOICE_STATUS.OUTSTANDING} onClick={() => handleTabChange(1)}>{msgTypes.INVOICE_STATUS.OUTSTANDING}</ToggleButton>
        <ToggleButton className='invoice-button button-danger' value={msgTypes.INVOICE_STATUS.OVERDUE} onClick={() => handleTabChange(2)}>{msgTypes.INVOICE_STATUS.OVERDUE}</ToggleButton>
      </ToggleButtonGroup>

      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={onSubmit}
      >
        {formik => (
          <div className={messageRead ? "read-msg" : "unread-msg"}>
            <Form>
            {owedInvoiceList ? (
              <CustomTableGrid
                item={
                  <FormikControl
                    control='materialTable'
                    tableColumn={columns}
                    tableData={owedInvoiceList}
                    // formikValue={formik}
                    setPagination={setPagination}
                 
                    exportExcel={exportAsExcel}
                  />
                }
              />
            ) : null}
            </Form>
          </div>
        )}
      </Formik>
      <Drawer open={open} anchor="right" onClose={toggleDrawer(false, new InvoiceManagementModel())} >
        <DisplayParticularInvoiceAsDrawer selectedInvoice={selectedInvoice} setOpen={setOpen} />
      </Drawer>
    </>
  );
};

export default OwedInvoice;
